import React, { useState } from 'react';
import { ArrowRight, CheckCircle, AlertCircle, Mail, Phone, User } from 'lucide-react';

const WaitlistSection = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsLoading(true);

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsLoading(false);
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <section id="waitlist-section" className="py-24 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-3xl p-12 shadow-2xl">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Welcome to the Founding Members!
            </h2>
            
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Thank you {formData?.fullName?.split(' ')?.[0]}! You're now part of ProConnect's exclusive founding community. We'll send you updates about our Tampa launch and your founding member benefits.
            </p>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">✓</div>
                <div className="text-sm text-gray-600">Founding Member Badge</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">$50</div>
                <div className="text-sm text-gray-600">Service Credit</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">∞</div>
                <div className="text-sm text-gray-600">Lifetime App Access</div>
              </div>
            </div>

            <p className="text-gray-500 text-sm">
              Check your email for your founding member welcome package and next steps.
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="waitlist-section" className="py-24 bg-gradient-to-br from-primary to-blue-800 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 right-20 w-32 h-32 bg-white/5 rounded-full blur-xl"></div>
        <div className="absolute bottom-40 left-16 w-24 h-24 bg-white/10 rounded-full blur-lg"></div>
      </div>
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl font-black text-white mb-8 leading-tight">
            Join the <span className="text-orange-300">Founding Members</span>
          </h2>
          
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed mb-8">
            Secure your exclusive position in ProConnect's founding community. 
            Limited to 1,000 members with lifetime benefits.
          </p>

          <div className="flex items-center justify-center gap-2 text-orange-300 font-medium text-lg">
            <AlertCircle className="w-5 h-5" />
            <span>Registration closes permanently at 1,000 members</span>
          </div>
        </div>

        {/* Registration Form - Simplified to match HeroSection */}
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-3xl p-8 shadow-2xl">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Secure Your Founding Member Spot
              </h3>
              <p className="text-gray-600 text-sm">
                Join the exclusive first 1,000 members
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Full Name Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <User className="w-4 h-4 inline mr-2" />
                  Full Name *
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData?.fullName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-gray-900 font-medium"
                  placeholder="Enter your full name"
                />
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData?.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-gray-900 font-medium"
                  placeholder="your@email.com"
                />
              </div>

              {/* Phone Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Phone className="w-4 h-4 inline mr-2" />
                  Phone Number *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData?.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-gray-900 font-medium"
                  placeholder="(555) 123-4567"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-primary to-blue-700 text-white font-bold text-lg px-8 py-4 rounded-xl hover:from-blue-700 hover:to-primary transition-all duration-300 transform hover:scale-105 shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-3">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Securing Your Spot...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-3">
                    Join the Founding Members
                    <ArrowRight className="w-5 h-5" />
                  </span>
                )}
              </button>

              <p className="text-gray-500 text-xs text-center">
                By registering, you agree to receive updates about ProConnect's launch and founding member benefits. 
                No spam, unsubscribe anytime.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WaitlistSection;